import { Injectable } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { LoginResponse, OidcSecurityService } from 'angular-auth-oidc-client';
import { SSO_PROVIDER } from 'app/shared/components/sso-provider-button/sso-provider-button.component';
import { environment } from 'env/environment';
import { Observable, Subject, Subscription } from 'rxjs';

const SSO_PROVIDER_CUSTOM_PARMS: Record<SSO_PROVIDER, any> = {
  'Google': {
    "prompt": 'select_account'
  },
};

const SSO_PROVIDER_CLIENT_ID: Map<SSO_PROVIDER, string> = new Map([
  ['Google', environment.googleClientId]
]);

export interface ISingleSignOnToken {
  SSOProvider: SSO_PROVIDER | null,
  Token: string
};

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class SingleSignOnService {
  private token$ = new Subject<ISingleSignOnToken>();
  private securitySubscription: Subscription | undefined;

  constructor(private readonly securityService: OidcSecurityService) { }

  public getSignOnToken(): Observable<ISingleSignOnToken> {
    return this.token$.asObservable();
  }

  public authorize(provider: SSO_PROVIDER, username?: string): void {
    const authOptions = {
      customParams: SSO_PROVIDER_CUSTOM_PARMS[provider] ? SSO_PROVIDER_CUSTOM_PARMS[provider] : {},
    }
    if (username) {
      authOptions.customParams.login_hint = username;
    }

    const config = this.lookupConfig(provider);
    if (config) {
      this.securityService.authorize(config, authOptions);
    }
  }

  public checkAuth(): void {
    SSO_PROVIDER_CLIENT_ID.forEach((clientId, provider) => {
      const configId = this.lookupConfig(provider);
      if (!configId) {
        return;
      }

      this.securityService.checkAuth(undefined, configId as string).subscribe((loginResponse: LoginResponse) => {
        const token = loginResponse.idToken;
        if (loginResponse.isAuthenticated) {
          this.token$.next({
            Token: token,
            SSOProvider: provider
          });
        } else {
          // signal they are not signed in
          this.token$.next({
            Token: '',
            SSOProvider: null
          });
        }
      });
    });
  }

  public logoutAll(): void {
    SSO_PROVIDER_CLIENT_ID.forEach((clientId, provider) => {
      const configId = this.lookupConfig(provider);
      if (!configId) {
        return;
      }
      if (this.securityService.isAuthenticated(configId)) {
        this.logout(provider);
        this.token$.next({
          Token: '',
          SSOProvider: null
        });
      }
    });
  }

  public logout(provider: SSO_PROVIDER): void {
    const config = this.lookupConfig(provider);
    if (config) {
      this.securityService.logoff(config);
    }
  }

  private lookupConfig(provider: SSO_PROVIDER): string | null {
    const clientId = SSO_PROVIDER_CLIENT_ID.get(provider);
    if (!clientId) {
      return null;
    }
    const configurations = this.securityService.getConfigurations().filter((obj) => {
      return obj.clientId === clientId;
    });
    return configurations[0].configId ? configurations[0].configId : null;
  }

  public checkForLogins(): void {
    SSO_PROVIDER_CLIENT_ID.forEach((clientId, provider) => {
      const configId = this.lookupConfig(provider);
      if (!configId) {
        return;
      }

      this.securitySubscription = this.securityService.getIdToken(configId as string).subscribe((token: string) => {
        if (token) {
          this.token$.next({
            Token: token,
            SSOProvider: provider
          });
        }
      });
    });
  }
}
